/**
 * Ad client.
 *
 * One provider fetches every slot for the current surface, then components read
 * from context. Rotation, viewability, frequency capping and the interstitial
 * cadence all run here so each ad component stays presentational.
 *
 * Honesty rules baked in: every unit is labelled "Ad", the interstitial always
 * gets a skip control once its timer elapses, and impressions are only counted
 * when the unit has actually been on screen.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { AdSlotResponse, ServedCreative } from '@fairwaylive/shared';
import { api, beacon, post } from './api.ts';

export type AdSurface = 'leaderboard' | 'bigscreen' | 'scorecard' | 'landing' | 'profile';

interface AdContextValue {
  slots: Record<string, AdSlotResponse>;
  isDemo: boolean;
  surface: AdSurface;
  loading: boolean;
  track: (creative: ServedCreative, eventType: 'impression' | 'viewable' | 'click' | 'skip' | 'complete' | 'close', dwellSeconds?: number) => void;
  clickThrough: (creative: ServedCreative) => string;
  /** Interstitial the provider wants shown right now, if any. */
  interstitial: ServedCreative | null;
  dismissInterstitial: (reason: 'skip' | 'complete' | 'close', dwellSeconds: number) => void;
}

const AdContext = createContext<AdContextValue | null>(null);

const CAP_KEY = 'fl_ad_caps';

/** Per-session impression counts, so the server can honour frequency caps. */
function readCaps(): Record<string, number> {
  try {
    return JSON.parse(sessionStorage.getItem(CAP_KEY) ?? '{}') as Record<string, number>;
  } catch {
    return {};
  }
}
function bumpCap(placementId: string): void {
  const caps = readCaps();
  caps[placementId] = (caps[placementId] ?? 0) + 1;
  sessionStorage.setItem(CAP_KEY, JSON.stringify(caps));
}

export interface AdProviderProps {
  surface: AdSurface;
  tournamentId?: string | null;
  organizationId?: string | null;
  isDemo?: boolean;
  /** Suppress the interstitial on surfaces where a takeover would be hostile. */
  allowInterstitial?: boolean;
  children: ReactNode;
}

export function AdProvider({
  surface,
  tournamentId = null,
  organizationId = null,
  isDemo = false,
  allowInterstitial = true,
  children,
}: AdProviderProps) {
  const [slots, setSlots] = useState<Record<string, AdSlotResponse>>({});
  const [loading, setLoading] = useState(true);
  const [interstitial, setInterstitial] = useState<ServedCreative | null>(null);
  const interstitialIndex = useRef(0);
  const shownAt = useRef<number>(0);

  const query = useMemo(() => {
    const params = new URLSearchParams({ surface });
    if (tournamentId) params.set('tournamentId', tournamentId);
    if (organizationId) params.set('organizationId', organizationId);
    if (isDemo) params.set('demo', 'true');
    params.set('seen', JSON.stringify(readCaps()));
    return params.toString();
  }, [surface, tournamentId, organizationId, isDemo]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api<{ slots: AdSlotResponse[] }>(`/api/ads/slots?${query}`)
      .then((payload) => {
        if (cancelled) return;
        setSlots(Object.fromEntries(payload.slots.map((slot) => [slot.zoneKey, slot])));
      })
      .catch(() => {
        // An ad failure must never break the page. Zones simply stay empty.
        if (!cancelled) setSlots({});
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [query]);

  const track = useCallback(
    (creative: ServedCreative, eventType: Parameters<AdContextValue['track']>[1], dwellSeconds?: number) => {
      if (eventType === 'impression') bumpCap(creative.placementId);
      const body = {
        token: creative.token,
        eventType,
        surface,
        ...(dwellSeconds === undefined ? {} : { dwellSeconds: Number(dwellSeconds.toFixed(2)) }),
        ...(tournamentId ? { tournamentId } : {}),
        ...(organizationId ? { organizationId } : {}),
      };
      // Unload-time events go by beacon; the rest are normal posts.
      if (eventType === 'close' || eventType === 'complete') beacon('/api/ads/events/beacon', body);
      else void post('/api/ads/events', body).catch(() => undefined);
    },
    [organizationId, surface, tournamentId],
  );

  const clickThrough = useCallback(
    (creative: ServedCreative) => {
      const params = new URLSearchParams({ token: creative.token, to: creative.clickUrl ?? '/', surface });
      if (tournamentId) params.set('tournamentId', tournamentId);
      return `/api/ads/click?${params.toString()}`;
    },
    [surface, tournamentId],
  );

  // ── Interstitial cadence ────────────────────────────────────────────────
  const popup = slots.popup_interstitial;
  useEffect(() => {
    if (!allowInterstitial || !popup?.enabled || !popup.creatives.length) return;
    const intervalMs = Math.max(30, popup.intervalSeconds ?? 120) * 1000;
    const show = () => {
      // Never interrupt a hidden tab — it would burn the impression unseen.
      if (document.visibilityState !== 'visible') return;
      const next = popup.creatives[interstitialIndex.current % popup.creatives.length];
      if (!next) return;
      interstitialIndex.current += 1;
      shownAt.current = Date.now();
      setInterstitial(next);
    };
    const timer = window.setInterval(show, intervalMs);
    // First takeover comes after one full interval, not on arrival.
    return () => window.clearInterval(timer);
  }, [allowInterstitial, popup]);

  const dismissInterstitial = useCallback(
    (reason: 'skip' | 'complete' | 'close', dwellSeconds: number) => {
      if (interstitial) track(interstitial, reason, dwellSeconds);
      setInterstitial(null);
    },
    [interstitial, track],
  );

  const value = useMemo<AdContextValue>(
    () => ({ slots, isDemo, surface, loading, track, clickThrough, interstitial, dismissInterstitial }),
    [clickThrough, dismissInterstitial, interstitial, isDemo, loading, slots, surface, track],
  );

  return <AdContext.Provider value={value}>{children}</AdContext.Provider>;
}

export function useAds(): AdContextValue {
  const context = useContext(AdContext);
  if (!context) {
    // Ad-free rendering is a valid state (e.g. a paid organizer's board).
    return {
      slots: {},
      isDemo: false,
      surface: 'leaderboard',
      loading: false,
      track: () => undefined,
      clickThrough: () => '/',
      interstitial: null,
      dismissInterstitial: () => undefined,
    };
  }
  return context;
}

/**
 * Rotates a zone's creatives and reports an impression once the unit has been
 * visible for a moment — not merely mounted, which is what makes the impression
 * numbers defensible to a sponsor.
 */
export function useRotatingCreative(zoneKey: string): {
  creative: ServedCreative | null;
  slot: AdSlotResponse | null;
  ref: (node: HTMLElement | null) => void;
} {
  const { slots, track } = useAds();
  const slot = slots[zoneKey] ?? null;
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(false);
  const nodeRef = useRef<HTMLElement | null>(null);
  const counted = useRef(new Set<string>());

  useEffect(() => {
    if (!slot?.creatives.length || slot.creatives.length < 2) return;
    const timer = window.setInterval(
      () => setIndex((i) => (i + 1) % slot.creatives.length),
      Math.max(5, slot.rotateSeconds) * 1000,
    );
    return () => window.clearInterval(timer);
  }, [slot]);

  const ref = useCallback((node: HTMLElement | null) => {
    nodeRef.current = node;
    if (!node || !('IntersectionObserver' in window)) {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) setVisible(true);
      },
      { threshold: 0.5 },
    );
    observer.observe(node);
  }, []);

  const creative = slot?.creatives[index] ?? null;

  useEffect(() => {
    if (!creative || !visible) return;
    if (counted.current.has(creative.placementId)) return;
    counted.current.add(creative.placementId);
    const timer = window.setTimeout(() => {
      track(creative, 'impression');
      track(creative, 'viewable');
    }, 400);
    return () => window.clearTimeout(timer);
  }, [creative, track, visible]);

  return { creative, slot, ref };
}
